import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Input, Modal, Select, Toggle, useToast } from '@w7/ui'
import {
  cmApi,
  leaguesFromListResult,
  summarizeOkResult,
  type CmSport,
  type CompetitionLeague,
  type CricketMarketGroups,
} from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { FetchResultPanel, type CmFetchEnvelope } from '@/features/fetch/FetchResultPanel'
import { toFetchEnvelope } from '@/features/fetch/fetchEnvelope'
import { CmPanel, SPORTS } from './CmPanel'
import { CatalogWorkflow } from './catalogGuide'
import {
  CRICKET_OFFER_OPTIONS,
  DEFAULT_CRICKET_GROUPS,
  cricketGroupsFromLeague,
} from './cricketMarketGroups'

const OFFSETS = [
  { value: '0', label: '0 min' },
  { value: '15', label: '15 min' },
  { value: '20', label: '20 min' },
  { value: '30', label: '30 min' },
]

type ModalForm = {
  maxBet: string
  maxMarket: string
  goalMarkets: string
  goalMaxBet: string
  goalMaxMarket: string
  offset: string
  matchOddsOnly: boolean
  cricketGroups: Required<CricketMarketGroups>
}

const emptyForm = (): ModalForm => ({
  maxBet: '',
  maxMarket: '',
  goalMarkets: '',
  goalMaxBet: '',
  goalMaxMarket: '',
  offset: '0',
  matchOddsOnly: false,
  cricketGroups: { ...DEFAULT_CRICKET_GROUPS },
})

function formFromLeague(league: CompetitionLeague): ModalForm {
  return {
    maxBet: league.maxBet != null ? String(league.maxBet) : '',
    maxMarket: league.maxMarket != null ? String(league.maxMarket) : '',
    goalMarkets: league.goalMarkets != null ? String(league.goalMarkets) : '',
    goalMaxBet: league.goalMaxBet != null ? String(league.goalMaxBet) : '',
    goalMaxMarket: league.goalMaxMarket != null ? String(league.goalMaxMarket) : '',
    offset:
      league.marketStartOffsetMinutes != null
        ? String(league.marketStartOffsetMinutes)
        : '0',
    matchOddsOnly: Boolean(league.isMatchOddsOnly),
    cricketGroups: cricketGroupsFromLeague(league),
  }
}

function numOrNull(v: string): number | null {
  if (v.trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export function CompetitionPage() {
  const toast = useToast()
  const qc = useQueryClient()
  const [urlParams] = useSearchParams()
  const deepLinkHandled = useRef(false)
  const [sport, setSport] = useState<CmSport>('soccer')
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<CompetitionLeague | null>(null)
  const [form, setForm] = useState<ModalForm>(emptyForm())
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [fetchEnvelope, setFetchEnvelope] = useState<CmFetchEnvelope | null>(null)

  const listQ = useQuery({
    queryKey: ['cm', 'competitions', sport],
    queryFn: async () => {
      const res = await cmApi.listCompetitions(authHeaders(), sport)
      return leaguesFromListResult(res)
    },
  })

  const leagues = useMemo(() => {
    const rows = listQ.data ?? []
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        String(l.betfairId ?? '').includes(q) ||
        String(l.vendorLeagueId ?? '').includes(q),
    )
  }, [listQ.data, query])

  const flaggedCount = (listQ.data ?? []).filter((l) => l.isCompetitionFlagged).length

  const openModal = (league: CompetitionLeague) => {
    setSelected(league)
    setForm(formFromLeague(league))
    setConfirmRemove(false)
  }

  useEffect(() => {
    const sportParam = urlParams.get('sport')
    if (sportParam === 'soccer' || sportParam === 'tennis' || sportParam === 'cricket') {
      setSport(sportParam)
    }
  }, [urlParams])

  useEffect(() => {
    const leagueId = urlParams.get('leagueId')
    if (!leagueId || deepLinkHandled.current || !listQ.data?.length) return
    const league = listQ.data.find((l) => l.id === leagueId)
    if (league) {
      deepLinkHandled.current = true
      openModal(league)
    }
  }, [urlParams, listQ.data])

  const refresh = useMutation({
    mutationFn: () => cmApi.fetchCompetitions(authHeaders(), sport),
    onSuccess: (data) => {
      setFetchEnvelope(toFetchEnvelope(data, sport))
      toast.success(summarizeOkResult('Competitions refresh', data))
      void qc.invalidateQueries({ queryKey: ['cm', 'competitions', sport] })
    },
    onError: (err: Error) => {
      setFetchEnvelope(null)
      toast.error(err.message)
    },
  })

  const save = useMutation({
    mutationFn: async (flagged: boolean) => {
      if (!selected) throw new Error('No competition selected')
      const body: Record<string, unknown> = {
        sport,
        leagueId: selected.id,
        isCompetitionFlagged: flagged,
        maxBet: numOrNull(form.maxBet),
        maxMarket: numOrNull(form.maxMarket),
        marketStartOffsetMinutes: Number(form.offset) || 0,
      }
      if (sport === 'soccer') {
        body.isMatchOddsOnly = form.matchOddsOnly
        if (form.matchOddsOnly) {
          body.goalMarkets = 0
          body.goalMaxBet = 0
          body.goalMaxMarket = 0
        } else {
          body.goalMarkets = numOrNull(form.goalMarkets)
          body.goalMaxBet = numOrNull(form.goalMaxBet)
          body.goalMaxMarket = numOrNull(form.goalMaxMarket)
        }
      }
      if (sport === 'cricket') {
        body.cricketMarketGroups = { ...form.cricketGroups }
      }
      return cmApi.competitionConfig(authHeaders(), body)
    },
    onSuccess: (_data, flagged) => {
      toast.success(flagged ? 'Added to automation' : 'Removed from automation')
      setSelected(null)
      setConfirmRemove(false)
      void qc.invalidateQueries({ queryKey: ['cm', 'competitions', sport] })
    },
    onError: (err: Error) => toast.error(err.message),
  })

  const validateSave = (): string | null => {
    const maxBet = numOrNull(form.maxBet)
    const maxMarket = numOrNull(form.maxMarket)
    if (maxBet == null || maxBet <= 0) return 'Max bet amount is required'
    if (maxMarket == null || maxMarket <= 0) return 'Max market amount is required'
    if (maxBet > maxMarket) return 'Max bet must be ≤ max market'
    if (sport === 'soccer' && !form.matchOddsOnly) {
      const gBet = numOrNull(form.goalMaxBet)
      const gMkt = numOrNull(form.goalMaxMarket)
      const gMarkets = numOrNull(form.goalMarkets)
      if (gBet == null || gBet <= 0) return 'Goal max bet is required'
      if (gMkt == null || gMkt <= 0) return 'Goal max market is required'
      if (gBet > gMkt) return 'Goal max bet must be ≤ goal max market'
      if (gMarkets == null || gMarkets < 1 || gMarkets > 9) {
        return 'Goal markets must be between 1 and 9'
      }
    }
    return null
  }

  const onSave = (e: FormEvent) => {
    e.preventDefault()
    const err = validateSave()
    if (err) {
      toast.error(err)
      return
    }
    save.mutate(true)
  }

  return (
    <CmPanel
      title="Competition selection"
      subtitle="Flag leagues for automation and set default max bet limits — soccer, tennis, cricket"
      actions={
        <Button
          type="button"
          variant="secondary"
          busy={refresh.isPending}
          onClick={() => refresh.mutate()}
        >
          Refresh from Betfair
        </Button>
      }
    >
      <CatalogWorkflow
        steps={[
          {
            title: 'Refresh',
            body: 'Pull the latest competition list from Betfair for the selected sport.',
          },
          {
            title: 'Add to automation',
            body: 'Pick a league, set max bet / max market (and soccer goal markets if needed), then save.',
          },
          {
            title: 'Cron picks it up',
            body: 'Flagged leagues are fetched automatically when Automation is on for that sport.',
          },
        ]}
      />

      <div className="cm-ms-toolbar">
        <Field label="Sport" hint="Each sport has its own competition list and automation flag.">
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={sport}
              onChange={(ev) => {
                setSport(ev.target.value as CmSport)
                setQuery('')
              }}
              options={[...SPORTS]}
            />
          )}
        </Field>
        <Field label="Search" hint="Filter by competition name or Betfair competition id.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={query}
              onChange={(ev) => setQuery(ev.target.value)}
              placeholder="Competition name or Betfair id"
            />
          )}
        </Field>
        <div className="cm-ms-meta" aria-live="polite">
          {listQ.isFetching ? (
            <span>Loading…</span>
          ) : (
            <span>
              {leagues.length} shown
              {flaggedCount ? ` · ${flaggedCount} in automation` : ''}
            </span>
          )}
        </div>
      </div>

      <p className="cm-hint">
        Leagues marked <strong>In automation</strong> are included when the auto-fetch worker runs.
        Edit limits anytime; remove automation with the button inside the modal.
      </p>

      <div className="cm-ms-list" role="list">
        {listQ.isError ? (
          <div className="cm-ms-empty">Could not load competitions. Try refresh.</div>
        ) : null}
        {!listQ.isFetching && !leagues.length && !listQ.isError ? (
          <div className="cm-ms-empty">
            No competitions yet. Click <strong>Refresh competitions</strong> to pull from Betfair.
          </div>
        ) : null}
        {leagues.map((league) => (
          <div key={league.id} className="cm-ms-row" role="listitem">
            <div className="cm-ms-row-main">
              <span className="cm-ms-name">{league.name}</span>
              <span className="cm-ms-id">Betfair {league.betfairId}</span>
              {league.isCompetitionFlagged ? (
                <span className="cm-ms-badge">In automation</span>
              ) : null}
            </div>
            <Button type="button" variant="primary" size="sm" onClick={() => openModal(league)}>
              {league.isCompetitionFlagged ? 'Edit automation' : 'Add to automation'}
            </Button>
          </div>
        ))}
      </div>

      <Modal
        open={!!selected}
        onClose={() => {
          if (save.isPending) return
          setSelected(null)
          setConfirmRemove(false)
        }}
        title={selected?.isCompetitionFlagged ? 'Edit automation' : 'Add to automation'}
        subtitle={selected?.name}
        size="md"
        closeDisabled={save.isPending}
        footer={
          <div className="cm-ms-modal-actions">
            <Button type="submit" form="cm-automation-form" variant="primary" busy={save.isPending}>
              Save
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={save.isPending}
              onClick={() => setSelected(null)}
            >
              Close
            </Button>
            {selected?.isCompetitionFlagged ? (
              confirmRemove ? (
                <Button
                  type="button"
                  variant="danger"
                  busy={save.isPending}
                  onClick={() => save.mutate(false)}
                >
                  Confirm remove
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={save.isPending}
                  onClick={() => setConfirmRemove(true)}
                >
                  Remove automation
                </Button>
              )
            ) : null}
          </div>
        }
      >
        <form id="cm-automation-form" className="cm-form" onSubmit={onSave}>
          <div className="cm-form-grid">
            <Field
              label="Max bet amount"
              required
              hint="Default max stake per bet for markets auto-created in this league."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={form.maxBet}
                  onChange={(ev) => setForm((f) => ({ ...f, maxBet: ev.target.value }))}
                  inputMode="decimal"
                />
              )}
            </Field>
            <Field
              label="Max market amount"
              required
              hint="Default max market liability. Must be ≥ max bet."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={form.maxMarket}
                  onChange={(ev) => setForm((f) => ({ ...f, maxMarket: ev.target.value }))}
                  inputMode="decimal"
                />
              )}
            </Field>
            <Field
              label="Market start offset"
              hint="Shift market open time by this many minutes after the scheduled kick-off."
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={form.offset}
                  onChange={(ev) => setForm((f) => ({ ...f, offset: ev.target.value }))}
                  options={OFFSETS}
                />
              )}
            </Field>
          </div>

          {sport === 'soccer' ? (
            <>
              <label className="cm-toggle">
                <Toggle
                  label="Match odds only"
                  checked={form.matchOddsOnly}
                  onChange={(v) => setForm((f) => ({ ...f, matchOddsOnly: v }))}
                />
                <span>Match odds only (skip goal markets)</span>
              </label>
              {!form.matchOddsOnly ? (
                <div className="cm-form-grid">
                  <Field label="Goal max bet" required>
                    {({ id, describedBy }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        value={form.goalMaxBet}
                        onChange={(ev) => setForm((f) => ({ ...f, goalMaxBet: ev.target.value }))}
                        inputMode="decimal"
                      />
                    )}
                  </Field>
                  <Field label="Goal max market" required>
                    {({ id, describedBy }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        value={form.goalMaxMarket}
                        onChange={(ev) =>
                          setForm((f) => ({ ...f, goalMaxMarket: ev.target.value }))
                        }
                        inputMode="decimal"
                      />
                    )}
                  </Field>
                  <Field label="Goal markets (1–9)" required>
                    {({ id, describedBy }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        value={form.goalMarkets}
                        onChange={(ev) => setForm((f) => ({ ...f, goalMarkets: ev.target.value }))}
                        inputMode="numeric"
                      />
                    )}
                  </Field>
                </div>
              ) : null}
            </>
          ) : null}

          {sport === 'cricket' ? (
            <fieldset className="cm-form-grid" style={{ gridColumn: '1 / -1' }}>
              <legend className="cm-hint" style={{ marginBottom: 8 }}>
                Offer Betfair market groups (unchecked = not fetched / not enabled)
              </legend>
              {CRICKET_OFFER_OPTIONS.map(({ key, label }) => (
                <label key={key} className="cm-toggle">
                  <Toggle
                    label={label}
                    checked={form.cricketGroups[key]}
                    onChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        cricketGroups: { ...f.cricketGroups, [key]: v },
                      }))
                    }
                  />
                  <span>{label}</span>
                </label>
              ))}
            </fieldset>
          ) : null}
        </form>
      </Modal>

      <FetchResultPanel
        data={fetchEnvelope}
        emptyLabel="Refresh result appears here after a successful Betfair competition fetch."
        aria-label="competition-fetch-result"
      />
    </CmPanel>
  )
}
